import BN from 'bignumber.js';
import {
  STAKE_SETTINGS,
  STAKE_DETAILS,
  STAKE_SEND,
  STAKE_SUCCESS,
  STAKE_ERROR,
  RESET_STAKE,
  STAKE_BALANCE,
  STAKE_TIME,
  UNSTAKED_BALANCE,
  REWARD_EARNED,
  UNSTAKE_TIME,
  REWARD_BALANCE,
  UNSTAKE_SETTINGS,
  UNSTAKE_INFORMATION,
  UNSTAKE_DETAILS,
  UNSTAKE_SEND,
  UNSTAKE_SUCCESS,
  WITHDRAW_DETAILS,
  WITHDRAW_SEND,
  WITHDRAW_SUCCESS,
  HISTORY_SHOW_OM_STAKING,
  MIGRATE_DETAILS,
  MIGRATE_SEND,
  MIGRATE_SUCCESS,
  MIGRATE_CHANGE_STATUS,
} from './action-types';

import { getOmBalance, approve } from './token-actions';
import omTokenContractService from '../services/contracts/om-token-contract-service';
import omStakingContractService from '../services/contracts/om-staking-contract-service';
import omStakingMigrationContractService from '../services/contracts/om-staking-migration-contract-service';
import config from '../config/env';
import { OM, OM_STAKING_MIGRATE_STATUS } from '../constants/blockchain-constants';

export const getMigrateStatus = async (address) => {
  if (localStorage.getItem(`${address}_notShowOmStakingItem`)) {
    return OM_STAKING_MIGRATE_STATUS.HIDE_POOL;
  }

  const [tokenBalance, stakeBalance, unstakedTime, accounts] = await Promise.all([
    omTokenContractService.balanceOf(address),
    omStakingContractService.balanceOf(address),
    omStakingContractService.unstakingTimeOf(address),
    omStakingMigrationContractService.accounts(address),
  ]);

  const { lockedPrice, lockedAmountPaid } = accounts;

  if (tokenBalance === '0' && stakeBalance === '0' && unstakedTime === '0') {
    localStorage.setItem(`${address}_notShowOmStakingItem`, '1');
    return OM_STAKING_MIGRATE_STATUS.HIDE_POOL;
  }

  if (stakeBalance !== '0') {
    return OM_STAKING_MIGRATE_STATUS.NEED_RUN_MIGRATE;
  }

  if (
    !lockedAmountPaid &&
    lockedPrice.mantissa !== '0' &&
    stakeBalance === '0' &&
    unstakedTime !== '0'
  ) {
    return OM_STAKING_MIGRATE_STATUS.MIGRATED_FIRST_STEP;
  }

  if (
    (lockedAmountPaid &&
      new BN(unstakedTime).plus(60 * 60 * 24).isGreaterThan(new Date().getTime() / 1000)) ||
    (lockedAmountPaid && tokenBalance !== '0')
  ) {
    return OM_STAKING_MIGRATE_STATUS.MIGRATE_FINISHED;
  }

  if (tokenBalance !== '0') {
    return OM_STAKING_MIGRATE_STATUS.NEED_RUN_MIGRATE;
  }

  localStorage.setItem(`${address}_notShowOmStakingItem`, '1');
  return OM_STAKING_MIGRATE_STATUS.HIDE_POOL;
};

export const updateBalances = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const [stakeBalance, rewardEarned, rewardBalance, unstakedTime] = await Promise.all([
    omStakingContractService.balanceOf(address),
    omStakingContractService.earned(address),
    omStakingContractService.rewardBalanceOf(address),
    omStakingContractService.unstakingTimeOf(address),
    dispatch(getOmBalance()),
  ]);

  // TODO: make one dispatch
  dispatch({ type: STAKE_BALANCE, payload: { stakeBalance } });
  dispatch({ type: REWARD_EARNED, payload: { rewardEarned } });
  dispatch({ type: REWARD_BALANCE, payload: { rewardBalance } });
  dispatch({ type: UNSTAKE_TIME, payload: { unstakedTime } });
};

// STAKE

export const stakeSettings = () => async (dispatch) => {
  dispatch({ type: STAKE_SETTINGS });
};

export const stakeDetails = (formValues) => async (dispatch) => {
  dispatch({ type: STAKE_DETAILS, payload: formValues });
};

export const stakeSend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;
  const { amount } = formValues;
  try {
    dispatch({ type: STAKE_SEND });
    await approve(OM, address, config.REACT_APP_OM_STAKING_ADDRESS);
    const transaction = await omStakingContractService.stake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: STAKE_SUCCESS, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR, payload: { error: 'Stake failed' } });
  }
};

// UNSTAKE

export const unstakeSettings = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_SETTINGS });
};

export const unstakeInformation = (formValues) => async (dispatch) => {
  dispatch({ type: UNSTAKE_INFORMATION, payload: formValues });
};

export const unstakeDetails = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_DETAILS });
};

export const unstakeSend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: UNSTAKE_SEND });

  const { amount } = formValues;

  try {
    const transaction = await omStakingContractService.unstake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: UNSTAKE_SUCCESS, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR, payload: { error: 'Unstake failed' } });
  }
};

export const withdrawUnstakedDetails = () => async (dispatch) => {
  dispatch({ type: WITHDRAW_DETAILS });
};

export const withdrawUnstakedSend = () => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: WITHDRAW_SEND });

  try {
    const unstakeBalance = await omStakingContractService.unstakingBalanceOf(address);
    const transaction = await omStakingContractService.withdrawUnstakedBalance(
      address,
      unstakeBalance,
    );
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: WITHDRAW_SUCCESS, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR, payload: { error: 'Unstake failed' } });
  }
};

export const stakeReset = () => async (dispatch) => {
  dispatch({ type: RESET_STAKE });
};

export const showHistory = () => async (dispatch) => {
  dispatch({ type: HISTORY_SHOW_OM_STAKING });
};

// VIEWS

export const getStakedBalance = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const stakeBalance = await omStakingContractService.balanceOf(address);
  dispatch({ type: STAKE_BALANCE, payload: { stakeBalance } });
};

export const getStakedTime = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const stakeTime = await omStakingContractService.stakeTime(address);
  dispatch({ type: STAKE_TIME, payload: { stakeTime } });
};

export const getUnstakedBalance = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const unstakedBalance = await omStakingContractService.unstakingBalanceOf(address);
  dispatch({ type: UNSTAKED_BALANCE, payload: { unstakedBalance } });
};

export const getUntakedTime = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const unstakedTime = await omStakingContractService.unstakingTimeOf(address);
  dispatch({ type: UNSTAKE_TIME, payload: { unstakedTime } });
};

export const getRewardsEarned = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const rewardEarned = await omStakingContractService.earned(address);
  dispatch({ type: REWARD_EARNED, payload: { rewardEarned } });
};

export const getRewardsBalance = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const rewardBalance = await omStakingContractService.rewardBalanceOf(address);
  dispatch({ type: REWARD_BALANCE, payload: { rewardBalance } });
};

export const migrateDetails = () => async (dispatch) => {
  dispatch({ type: MIGRATE_DETAILS });
};

export const changeMigrateStatus = (status) => async (dispatch) => {
  dispatch({ type: MIGRATE_CHANGE_STATUS, payload: { migrateStatus: status } });
};

export const migrateFirstStepSend = () => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: MIGRATE_SEND });

  try {
    const accounts = await omStakingMigrationContractService.accounts(address);
    if (accounts.lockedPrice.mantissa === '0') {
      await omStakingMigrationContractService.lockStakingPoolV1Rewards(address);
    }

    const stakeBalance = await omStakingContractService.balanceOf(address);
    if (stakeBalance === '0') {
      throw new Error('Stake balance is empty');
    }

    const transaction = await omStakingContractService.exit(address);

    await dispatch(updateBalances());
    const migrateStatus = await getMigrateStatus(address);
    dispatch(changeMigrateStatus(migrateStatus));

    // COMPLETE
    dispatch({ type: MIGRATE_SUCCESS, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR, payload: { error: 'Migrate failed' } });
  }
};

export const migrateSecondStepSend = () => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: MIGRATE_SEND });

  try {
    let accounts = await omStakingMigrationContractService.accounts(address);

    if (accounts.requiredAmount === '0') {
      await omStakingMigrationContractService.lockStakingPoolV2Rewards(address);
    }

    const unstakingBalanceOf = await omStakingContractService.unstakingBalanceOf(address);
    if (unstakingBalanceOf !== '0') {
      await omStakingContractService.withdrawUnstakedBalance(address, unstakingBalanceOf);
    }

    accounts = await omStakingMigrationContractService.accounts(address);
    if (accounts.lockedAmountPaid) {
      throw new Error('Migrate is finished');
    }

    await approve(OM, address, config.REACT_APP_OM_STAKING_MIGRATION_ADDRESS);

    const transaction = await omStakingMigrationContractService.migrate(address);

    await dispatch(updateBalances());
    const migrateStatus = await getMigrateStatus(address);
    dispatch(changeMigrateStatus(migrateStatus));

    // COMPLETE
    dispatch({ type: MIGRATE_SUCCESS, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR, payload: { error: 'Migrate failed' } });
  }
};

export const initData = () => async (dispatch) =>
  Promise.all([
    dispatch(getUntakedTime()),
    dispatch(getStakedBalance()),
    dispatch(getStakedTime()),
    dispatch(getUnstakedBalance()),
    dispatch(getRewardsEarned()),
    dispatch(getRewardsBalance()),
  ]);
